// PROVENANCE VIEW for ONE lead's outreach draft — the flowchart of how the
// email was composed, plus a details panel, opened by the arrow on a lead card.
//
// It is the sibling of AgentRunView. That view answers "how did this company
// end up on the list?"; this one answers "why does the email say what it
// says?". They share the same picture — FlowCanvas, the gridded rectangles with
// a details panel to the right — because they are the same interaction; only
// the graph and the chrome differ. This one never talks about kept/dropped
// leads, an audience list or a campaign, because a draft has none of those. It
// talks about the steps that produced a paragraph.
//
// PURELY PRESENTATIONAL. The passes come from lib/draftProvenance.js, the email
// from lib/outreachDraft.js (already humanized by the card). Nothing here
// sends, browses or mutates — the whole app stages drafts a human carries out.
import { useEffect, useMemo, useRef, useState } from 'react'
import FlowCanvas, { passIcon } from './FlowCanvas.jsx'
import SourcesChip from './SourcesChip.jsx'
import { ArrowLeftIcon } from './icons.jsx'
import { buildNarrative } from '../lib/draftNarrative.js'
import { readResearch } from '../lib/outreachDraft.js'
import { lambdaSource } from '../lib/enrich.js'
import { browserExecSource } from '../lib/sourceOf.js'

// ── THE CODE THAT RAN — the Developer view's reason to exist ────────────────
//
// Every pass carries `exec`: the execution records for that step (see
// lib/draftProvenance.js#runExec). Two kinds, and BOTH are shown as source
// text, not as a description of it:
//
//   aws-lambda  a record the lambda itself returned at run time — the draft
//               route's on the email, the enrich-worker's on the dossier. Its
//               `calls` and `constants` name the functions and prompt
//               constants that ran; the panel asks GET /enrich/source for
//               their text — the handler.py the deployed function is executing
//               — and shows it, with the deployed sha held against the sha the
//               record carries (same = the exact revision that ran).
//   browser     the run node in src/lib/agentRun.js and the helpers it called.
//               Its source is the raw text of this build's own files
//               (lib/sourceOf.js), cut to the named definitions.
//
// The panel is code first. Everything the older view showed — the run's
// recorded sentence, the values held, the links, the email — is still here,
// folded under "Data this step held", because a reader who came to correct
// the code should meet the code.
const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }

// Legacy exec records (written before `constants` existed) still get the
// prompt constants their function is known to read.
const DEFAULT_CONSTANTS = {
  'revailo-sales-enrich': ['DRAFT_MODEL', 'DRAFT_PROMPT_ID', 'DRAFT_SYSTEM', '_TOUCH_HOOKS', '_LEAD_FIELDS', '_DOSSIER_FIELDS'],
  'revailo-sales-enrich-worker': [
    'RESEARCH_MODEL', 'SCORING_MODEL', 'SELLER_PROFILE', 'SYSTEM_PROMPT', 'PREAMBLE',
    'FINANCIAL_TASK', 'PEOPLE_TASK', 'PROJECTS_TASK', 'SYNTHESIS_TASK', 'SCORING_RUBRIC',
  ],
}

// One record → the source it names. Lambda records go to the API; browser
// records to the raw-source extractor. The answer is { symbols, missing, code?,
// file? } or an error the panel prints verbatim.
function useExecSource(rec) {
  const [state, setState] = useState({ status: 'loading', data: null, error: '' })
  const key = rec ? JSON.stringify([rec.runtime, rec.function, rec.fn, rec.file, rec.calls, rec.constants]) : ''
  useEffect(() => {
    let alive = true
    if (!rec) return undefined
    setState({ status: 'loading', data: null, error: '' })
    const run = async () => {
      if (rec.runtime === 'aws-lambda') {
        const fn = String(rec.function || '')
        const calls = (Array.isArray(rec.calls) ? rec.calls : []).filter((c) => c && c !== 'handler')
        const constants = Array.isArray(rec.constants) && rec.constants.length ? rec.constants : DEFAULT_CONSTANTS[fn] || []
        return lambdaSource(fn, [...calls, ...constants])
      }
      return browserExecSource(rec)
    }
    run()
      .then((data) => {
        if (alive) setState({ status: 'ready', data, error: '' })
      })
      .catch((err) => {
        if (alive) setState({ status: 'error', data: null, error: String(err?.message || 'could not load the source') })
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return state
}

// Source text with a line-number gutter that reads as the FILE's line numbers
// (the block starts at `line`), so a reader can go straight to the spot.
function CodeBlock({ source, line = 1 }) {
  const lines = useMemo(() => String(source || '').split('\n'), [source])
  const width = String(line + lines.length).length
  return (
    <div className="max-h-[560px] overflow-auto rounded-[8px] bg-[rgba(0,0,0,0.22)] [border:1px_solid_var(--border)]">
      <pre className="m-0 px-0 py-2 text-[12px] leading-[1.55] text-ink" style={MONO}>
        {lines.map((text, i) => (
          <div key={i} className="flex min-w-max px-3 hover:bg-[rgba(255,255,255,0.03)]">
            <span
              aria-hidden="true"
              className="flex-none select-none pr-4 text-right text-faint"
              style={{ width: `${width + 1}ch` }}
            >
              {line + i}
            </span>
            <span className="whitespace-pre">{text}</span>
          </div>
        ))}
      </pre>
    </div>
  )
}

function Chip({ children, tone = 'dim' }) {
  const cls =
    tone === 'primary'
      ? 'bg-primary-tint text-primary-dark'
      : tone === 'danger'
        ? 'bg-[rgba(220,38,38,0.12)] text-danger'
        : tone === 'good'
          ? 'bg-[rgba(34,197,94,0.14)] text-good'
          : 'bg-surface text-dim'
  return (
    <span className={`inline-flex items-center rounded-full px-[7px] py-px text-[10.5px] font-semibold ${cls}`}>
      {children}
    </span>
  )
}

// One execution record: a header naming what ran (runtime, function, file,
// model, prompt sha) and then the SOURCE of every symbol it names, each in
// its own foldable block with file line numbers.
function ExecSource({ rec }) {
  const lambda = rec.runtime === 'aws-lambda'
  const src = useExecSource(rec)
  const symbols = src.data?.symbols || []
  const missing = src.data?.missing || []
  const deployedSha = src.data?.code || ''
  const ranSha = rec.code || ''
  const shaState = lambda && deployedSha && ranSha ? (deployedSha === ranSha ? 'same' : 'changed') : ''
  const title = lambda ? rec.function || 'lambda' : rec.fn || (Array.isArray(rec.calls) ? rec.calls.join(' → ') : rec.file)
  return (
    <section className="rounded-[12px] bg-panel p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Chip tone={lambda ? 'primary' : 'dim'}>{lambda ? 'AWS Lambda · Python' : 'Browser · JavaScript'}</Chip>
        <span className="text-[13px] font-semibold text-ink" style={MONO}>
          {title}
        </span>
        {rec.version && <Chip>v{rec.version}</Chip>}
        {rec.model && <Chip>{rec.model}</Chip>}
        {rec.scoringModel && <Chip>scoring {rec.scoringModel}</Chip>}
        {rec.prompt?.id && (
          <Chip>
            {rec.prompt.id}
            {typeof rec.prompt.sha === 'string' ? ` #${rec.prompt.sha}` : ''}
          </Chip>
        )}
        {Number.isFinite(rec.ms) && <Chip>{(rec.ms / 1000).toFixed(1)}s</Chip>}
        {rec.fallback && <Chip tone="danger">fallback</Chip>}
        {rec.isPost && <Chip>then, in the browser</Chip>}
      </div>
      <div className="mt-1 text-[12px] text-dim" style={MONO}>
        {rec.file}
        {rec.at ? <span className="text-faint"> · ran {new Date(rec.at).toLocaleString()}</span> : null}
      </div>
      {rec.fallback && <p className="m-0 mt-1 text-[12px] leading-[1.5] text-danger">{rec.fallback}</p>}
      {rec.notes && <p className="m-0 mt-1 text-[12px] leading-[1.5] text-dim">{rec.notes}</p>}
      {shaState === 'same' && (
        <p className="m-0 mt-1 text-[12px] leading-[1.5] text-good">
          Deployed source #{deployedSha} is the exact revision that ran here — edit this file to change it.
        </p>
      )}
      {shaState === 'changed' && (
        <p className="m-0 mt-1 text-[12px] leading-[1.5] text-danger">
          The deployed source has CHANGED since this ran (ran #{ranSha}, deployed now #{deployedSha}) — what follows is
          today&apos;s code, not the revision that produced this.
        </p>
      )}
      {lambda && !ranSha && deployedSha && (
        <p className="m-0 mt-1 text-[12px] leading-[1.5] text-faint">
          This record carries no source sha (older run); showing the deployed source #{deployedSha}.
        </p>
      )}
      {!lambda && (
        <p className="m-0 mt-1 text-[12px] leading-[1.5] text-faint">
          Browser records carry no build sha to compare; what follows is the source of the build currently loaded,
          which may differ from the revision that produced this.
        </p>
      )}

      {src.status === 'loading' && (
        <p className="m-0 mt-2 text-[12px] text-dim">
          {lambda ? 'Fetching the deployed source…' : 'Loading the source…'}
        </p>
      )}
      {src.status === 'error' && (
        <p className="m-0 mt-2 text-[12px] leading-[1.5] text-danger">Could not load the source: {src.error}</p>
      )}
      {src.status === 'ready' && (
        <div className="mt-2 flex flex-col gap-y-2">
          {symbols.map((sym) => (
            <details key={`${sym.file || rec.file}:${sym.name}`} open className="group">
              <summary className="flex cursor-pointer list-none items-center gap-x-2 rounded-[6px] px-1 py-[3px] text-[12px] hover:bg-[rgba(255,255,255,0.04)]">
                <span aria-hidden="true" className="text-faint transition-transform group-open:rotate-90">
                  ▸
                </span>
                <span className="font-semibold text-ink" style={MONO}>
                  {sym.name}
                </span>
                <span className="text-faint" style={MONO}>
                  {sym.kind ? `${sym.kind} · ` : ''}
                  {sym.file && sym.file !== rec.file ? `${sym.file} · ` : ''}
                  lines {sym.line}–{sym.endLine}
                  {sym.partial ? ' · end not found, showing a window' : ''}
                </span>
              </summary>
              <div className="mt-1">
                <CodeBlock source={sym.source} line={sym.line} />
              </div>
            </details>
          ))}
          {missing.length > 0 && (
            <p className="m-0 text-[12px] leading-[1.5] text-faint" style={MONO}>
              not found in source: {missing.join(', ')}
            </p>
          )}
          {!symbols.length && !missing.length && (
            <p className="m-0 text-[12px] text-dim">This record names no symbols to show.</p>
          )}
        </div>
      )}
    </section>
  )
}

// A pass's records, flattened: a lambda record's `post` steps (the browser's
// humanize pass after the model) become their own browser records right after
// it, so the reader sees everything that touched the text, in order.
function flattenExec(exec) {
  const out = []
  for (const rec of Array.isArray(exec) ? exec : []) {
    if (!rec || typeof rec !== 'object') continue
    out.push(rec)
    for (const p of Array.isArray(rec.post) ? rec.post : []) {
      if (p && typeof p === 'object') out.push({ ...p, isPost: true })
    }
  }
  return out
}

// The email, in the same Arial/Helvetica stack outreachDraft.js writes into
// the HTML, but THEMED like the rest of the app — a dark card in dark mode —
// because this box is for reading, not sending. The clipboard copy on the lead
// card carries draft.bodyHtml, which stays standard black-on-white email
// markup regardless of what this preview looks like. Read-only — it is a
// picture of the draft, not an editor.
function EmailPreview({ draft }) {
  const paragraphs = useMemo(
    () => String(draft?.bodyText || '').split(/\n{2,}/).filter(Boolean),
    [draft],
  )
  if (!draft?.bodyText) return null
  return (
    <div className="mt-[14px] overflow-hidden rounded-[12px] bg-surface [border:1px_solid_var(--border)]">
      <div className="[border-bottom:1px_solid_var(--border)] px-[14px] py-[10px]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-faint">Subject</div>
        <div
          className="mt-[3px] text-[14px] font-semibold text-ink"
          style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
        >
          {draft.subject}
        </div>
      </div>
      <div className="px-[14px] py-[12px]">
        {paragraphs.map((p, i) => (
          <p
            key={i}
            className="m-0 mb-[12px] last:mb-0 whitespace-pre-line text-[14px] leading-[1.5] text-ink"
            style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
          >
            {p}
          </p>
        ))}
      </div>
    </div>
  )
}

// The call-prep panel — what a CALL touch shows instead of an email preview:
// the talking points buildCallPoints produced, and the best number actually
// held. The tel: link is the one contact affordance the app allows — a link
// the USER taps on their own phone; the app itself never dials.
function CallPrepPanel({ meta }) {
  const points = Array.isArray(meta?.points) ? meta.points : []
  const phone = String(meta?.phone ?? '').trim()
  if (!points.length && !phone) return null
  return (
    <div className="overflow-hidden rounded-[12px] bg-surface [border:1px_solid_var(--border)]">
      <div className="[border-bottom:1px_solid_var(--border)] px-[14px] py-[10px]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-faint">
          Best number
        </div>
        <div className="mt-[3px] text-[14px] font-semibold text-ink">
          {phone ? (
            <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="text-primary no-underline hover:underline">
              {phone}
            </a>
          ) : (
            <span className="text-dim">None on file — Bo finds the number</span>
          )}
        </div>
      </div>
      {points.length > 0 && (
        <ol className="m-0 flex list-none flex-col gap-y-[9px] p-[14px]">
          {points.map((p, i) => (
            <li key={i} className="flex items-start gap-x-[9px]">
              <span className="mt-px flex-none text-[12px] font-bold tabular-nums text-faint">
                {i + 1}.
              </span>
              <span className="text-[13.5px] leading-[1.5] text-ink">{p}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

// ── the Main | Developer toggle ─────────────────────────────────────────────
//
// Two real buttons, aria-pressed, house chrome. "Main" is the big-type story
// (lib/draftNarrative.js); "Developer" is the flowchart this file has always
// drawn. Main is the default on EVERY open — no persistence — because the
// story is the view a rep reads, and the graph is the view a builder checks.
function ViewToggle({ view, onChange }) {
  const seg = (value, label) => {
    const active = view === value
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => onChange(value)}
        className={
          active
            ? 'cursor-pointer rounded-[7px] bg-primary px-3 py-[5px] text-[13px] font-semibold text-white [border:none]'
            : 'cursor-pointer rounded-[7px] bg-transparent px-3 py-[5px] text-[13px] font-medium text-dim transition-colors hover:text-ink [border:none]'
        }
      >
        {label}
      </button>
    )
  }
  return (
    <div
      role="group"
      aria-label="How to read this view"
      className="inline-flex flex-none items-center gap-x-[2px] rounded-[10px] bg-surface p-[3px] [border:1px_solid_var(--border)]"
    >
      {seg('main', 'Main')}
      {seg('developer', 'Developer')}
    </div>
  )
}

// ── the main view — the Apple-marketing-site read of the same facts ─────────
//
// Big sentences, one thought per line, entering on an opacity/blur/translate
// ramp (@keyframes narrativeLineIn in styles.css). Sections below the fold
// wait for an IntersectionObserver to see them, once, and reduced motion gets
// everything fully visible with no animation and no blur — both here (the
// observer is skipped) and in the CSS (the media query zeroes the animation).

const BIG_LINE =
  'm-0 max-w-[21ch] text-[clamp(26px,4vw,44px)] font-bold leading-[1.15] tracking-[-0.02em] text-ink'
const DIM_LINE =
  'm-0 max-w-[34ch] text-[clamp(19px,2.6vw,28px)] font-semibold leading-[1.3] tracking-[-0.01em] text-dim'

function usePrefersReducedMotion() {
  return useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )
}

// Time-staggered, not scroll-triggered. The first cut watched each section
// with an IntersectionObserver, but IO callbacks run in the rendering steps —
// a backgrounded or hidden tab never runs those, so the whole story could sit
// at opacity 0 indefinitely. Three sections don't need scroll choreography:
// a fixed cascade (sections ~350ms apart, lines ~110ms apart inside each) is
// deterministic in every environment and reads exactly the same. Reduced
// motion renders everything visible with no animation — enforced both here
// (no -in class, no delays) and in the CSS media query.
function NarrativeSection({ section, index, reduced }) {
  return (
    <section aria-label={section.kicker}>
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-faint">
        {section.kicker}
      </div>
      <div className="mt-6 flex flex-col gap-y-5">
        {section.lines.map((line, i) => (
          <p
            key={i}
            className={`narrative-line ${reduced ? '' : 'narrative-line-in'} ${
              line.tone === 'dim' ? DIM_LINE : BIG_LINE
            }`}
            style={reduced ? undefined : { animationDelay: `${index * 350 + i * 110}ms` }}
          >
            {line.text}
          </p>
        ))}
      </div>
      {/* THE EXTERNAL EVIDENCE. On the researched section these are the pages
          the enrichment dossier actually opened — real links, opened in a new
          tab, labelled by hostname, so a reader can go and check the fact
          above instead of trusting it. buildNarrative only ever puts `links`
          on a section whose fact really came out of the research, and every
          href was protocol-checked (http/https only) before it got here. */}
      {Array.isArray(section.links) && section.links.length > 0 && (
        <>
          <ul className="mt-5 flex list-none flex-col gap-y-2 p-0">
            {section.links.map((l) => (
              <li key={l.href} className="m-0">
                <a
                  href={l.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-[15px] font-semibold text-primary underline decoration-dotted underline-offset-4"
                >
                  {l.host}
                </a>
              </li>
            ))}
          </ul>
          {/* The same chip the rest of the app uses, now an EXTERNAL-sources
              component: the full URLs behind those hostnames, on demand. It
              can only ever render links, so no internal provenance string
              ('Masonry directory import') can reach a surface labelled
              "Sources". */}
          <SourcesChip links={section.links} className="mt-4" />
        </>
      )}
    </section>
  )
}

function NarrativeView({ sections, footer, sceneKey }) {
  const reduced = usePrefersReducedMotion()
  return (
    // .no-scrollbar: the story scrolls, but shows no bar — this is a reading
    // surface, not a workbench (user request). Scrolling itself is unchanged.
    <div className="no-scrollbar mt-2 min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[840px] flex-col gap-y-16 px-1 pb-16 pt-10">
        {sections.map((section, index) => (
          // Keyed on the scene so swapping to another lead's draft replays the
          // entrance instead of inheriting a played-out one.
          <NarrativeSection
            key={`${sceneKey}:${section.id}`}
            section={section}
            index={index}
            reduced={reduced}
          />
        ))}
        <div>
          <span className="inline-flex items-center gap-x-[7px] rounded-[9px] bg-panel px-3 py-[7px] text-[12.5px] font-semibold text-dim">
            {footer}
          </span>
        </div>
      </div>
    </div>
  )
}

function PanelBlock({ label, children }) {
  return (
    <div className="mt-[14px] first:mt-0">
      <div className="mb-[6px] text-[12px] font-semibold text-dim">{label}</div>
      {children}
    </div>
  )
}

// The three stories this view can tell. Same picture, different chrome: an
// initial draft, a follow-up draft, or call prep. Every string here keeps the
// same promise — nothing sends, nothing dials; a human carries it out.
const KINDS = {
  initial: {
    title: 'How this draft got here',
    subtitle: 'The masonry outreach method, one draft at a time.',
    // THREE STATES, ONE SENTENCE EACH — and no cross-reference to a section by
    // name: the researched section only exists when there is a fact to show, so
    // a "(see …)" pointer was sending readers to a heading that wasn't there.
    note: 'A draft, staged for a human to send. No web research ran on this lead, so its personal line comes off the directory row.',
    // For the lead whose search really did run and brought nothing back. "No
    // web search ran" would be false of it; "the draft uses the research" would
    // be false too — it uses the row.
    attemptedNote:
      'A draft, staged for a human to send. Web research ran on this lead and no dossier reached it, so the email is written from the directory row alone.',
    // Research DID run, on the live web, via the enrichment API.
    researchedNote:
      'A draft, staged for a human to send. Web research ran on the live web (OpenAI web search), and the email uses only what that research and the row actually hold.',
    terminalLabel: 'Ready to paste',
    defaultId: 'ev-draft',
    emailIds: ['ev-draft'],
    footer: 'Nothing was sent — staged for a human',
    summaryLine:
      'Every step that produced this email, in order. Select one to see the data it read and what the run recorded for this lead.',
  },
  followup: {
    title: 'How this draft got here',
    subtitle: 'The cadence method, one touch at a time.',
    note: 'A draft, staged for a human to send. The wait gate, reply freeze and touch number are computed from outreach history this workspace holds.',
    researchedNote:
      'A draft, staged for a human to send. The wait gate, reply freeze and touch number are computed from outreach history this workspace holds; the personal line came from web research.',
    terminalLabel: 'Ready to paste',
    defaultId: 'fu-draft',
    emailIds: ['fu-draft'],
    footer: 'Nothing was sent — staged for a human',
    summaryLine:
      'Every step that produced this touch, in order. Select one to see the data it read and what the run recorded for this lead.',
  },
  call: {
    title: 'How this call prep got here',
    subtitle: 'Bo’s call prep, one lead at a time.',
    note: 'Call prep, staged for a human to dial. The app never places a call — the number is a link you tap on your own phone.',
    terminalLabel: 'Bo dials — the app never calls',
    defaultId: 'call-points',
    emailIds: [],
    footer: 'The app never dials — Bo makes the call',
    summaryLine:
      'Every step that produced these talking points, in order. Select one to see the data it read.',
  },
}

/**
 * @param {object}   lead           { key, name, email } — the card's stub
 * @param {string}   operationLabel the operation this draft came out of
 * @param {object[]} passes         from buildDraftPasses() /
 *                                  buildFollowupDraftPasses() / buildCallPasses()
 * @param {object}   [draft]        humanized { subject, bodyText, bodyHtml } —
 *                                  absent for kind 'call'
 * @param {string}   [kind]         'initial' (default) | 'followup' | 'call'
 * @param {object}   [meta]         for 'call': { points, phone } (the same meta
 *                                  buildCallPasses was given)
 * @param {function} onBack         back to the thread
 */
export default function DraftFlowView({
  lead,
  operationLabel,
  passes = [],
  draft,
  facts = null,
  kind = 'initial',
  meta,
  onBack,
}) {
  const K = KINDS[kind] || KINDS.initial
  // Whether web research really ran on this lead, read off whichever holder
  // carries the signal — the intel snapshot (the engine's merged row) or the
  // receipt stub's bounded copies. Old receipts carry neither, read
  // { researched: false }, and render today's chrome verbatim.
  const researched = useMemo(() => {
    const fromFacts = readResearch(typeof facts === 'object' && facts ? facts : {})
    return fromFacts.researched || readResearch(lead || {}).researched
  }, [facts, lead])
  // The header disclaimer is conditional on that signal: "the app does not
  // browse" is only writable for a draft that truly wasn't researched, and
  // "no web search ran" only for one whose search was never run at all — a
  // lead whose research was started and came back empty gets its own line.
  const baseNote =
    researched && K.researchedNote
      ? K.researchedNote
      : lead?.researchAttempted && K.attemptedNote
        ? K.attemptedNote
        : K.note
  // WHO WROTE THE WORDS, in the header: the model on the server, or the
  // browser template (and, when the template stood in for the model, that it
  // did). Read off the draft's own exec record; an old draft with none gets the
  // base note unchanged.
  const wrote = draft?.exec && typeof draft.exec === 'object' ? draft.exec : null
  const note =
    kind === 'call'
      ? baseNote
      : wrote?.runtime === 'aws-lambda'
        ? `${baseNote} The words were written by ${wrote.model || 'the model'} on the server (${wrote.file || 'enrich/handler.py'}) from those facts — see the Developer view for the exact code and prompt.`
        : wrote?.runtime === 'browser'
          ? `${baseNote} The words were assembled by the browser template (${wrote.file || 'src/lib/outreachDraft.js'})${wrote.fallback ? ' as a fallback — the model did not write this one' : ''}.`
          : baseNote
  // Two reads of the same facts: 'main' is the big-type story, 'developer' is
  // the flowchart. Main on every open — deliberately NOT persisted.
  const [view, setView] = useState('main')
  // Open on the payload step, so the thing a rep came for — the email, or the
  // talking points — is the first thing in the panel; the provenance is the
  // "why", but the payload is the "what".
  const [selectedId, setSelectedId] = useState(K.defaultId)
  // The view stays mounted while Chat swaps which draft it shows; a selection
  // from a follow-up graph ('fu-draft') does not exist in a call graph, so the
  // selection snaps back to the payload step whenever the story changes — and
  // the toggle snaps back to Main on EVERY open (`passes` is rebuilt each time
  // the arrow is clicked, so its identity marks an open — even of the same
  // lead), because the default is the story, deliberately unpersisted.
  useEffect(() => {
    setSelectedId(K.defaultId)
  }, [kind, lead?.key, K.defaultId])
  useEffect(() => {
    setView('main')
  }, [passes])

  // The narrative is pure derivation from the same inputs the flowchart got —
  // lib/draftNarrative.js invents nothing, so both views can only ever tell
  // the same story.
  const narrative = useMemo(
    () => buildNarrative({ kind, lead, facts, draft, meta, passes }),
    [kind, lead, facts, draft, meta, passes],
  )
  const selected = selectedId ? passes.find((p) => p.id === selectedId) || null : null
  const total = passes.length
  const selectedIndex = selected ? passes.indexOf(selected) : -1
  const SelectedIcon = selected ? passIcon(selected.id) : null
  const showsEmail = kind !== 'call' && K.emailIds.includes(selectedId)
  const showsCallPrep = kind === 'call' && selectedId === 'call-points'

  // The execution records of the selected step, flattened for the code panel.
  const execRecords = useMemo(() => flattenExec(selected?.exec), [selected])

  return (
    <section className="flex h-full min-h-0 flex-col">
      <header className="flex-none">
        <div className="flex items-center justify-between gap-x-3">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to chat"
            className="inline-flex cursor-pointer items-center gap-x-2 rounded-[8px] bg-transparent px-1 py-1 text-[13px] font-medium text-dim transition-colors hover:text-ink [border:none]"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Back</span>
          </button>
          <ViewToggle view={view} onChange={setView} />
        </div>

        <div className="mt-2 min-w-0">
          <h1 className="m-0 text-[21px] font-bold tracking-[-0.02em] text-ink">
            {K.title}
          </h1>
          <p className="m-0 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] leading-[1.55] text-dim">
            <span>{K.subtitle}</span>
            {lead?.name && (
              <span className="rounded-full bg-primary-tint px-[8px] py-px text-[11px] font-semibold text-primary-dark">
                {lead.name}
              </span>
            )}
            {operationLabel && <span className="text-[12px] text-faint">· {operationLabel}</span>}
          </p>
        </div>

        <p className="m-0 mt-3 text-[12px] leading-[1.5] text-faint">{note}</p>
      </header>

      {view === 'main' ? (
        <NarrativeView
          sections={narrative}
          footer={K.footer}
          sceneKey={`${kind}:${lead?.key || lead?.name || ''}`}
        />
      ) : (
      <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        {/* THE FLOWCHART, slim: it is the index into the code, not the
            content. A fixed share of the width, scaled to fit, and the code
            panel takes the rest. */}
        <div className="relative min-h-[220px] lg:h-auto lg:w-[34%] lg:min-w-[340px] lg:max-w-[520px] lg:flex-none">
          <FlowCanvas
            passes={passes}
            terminalLabel={K.terminalLabel}
            terminalDone
            selectedId={selectedId}
            onSelect={setSelectedId}
            fit
          />
        </div>

        <aside
          aria-label={
            selected
              ? `${selected.title} — the code that ran`
              : kind === 'call'
                ? 'Call prep summary'
                : 'Draft summary'
          }
          className="flex max-h-[62%] w-full min-w-0 flex-1 flex-col overflow-hidden rounded-[16px] bg-surface shadow-card [border:1px_solid_var(--border)] lg:max-h-none"
        >
          <div className="flex flex-none items-start justify-between gap-x-3 px-5 py-[14px] [border-bottom:1px_solid_var(--border)]">
            <div className="flex min-w-0 items-start gap-x-[11px]">
              {SelectedIcon && (
                <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-[9px] bg-primary-tint text-primary [&_svg]:h-[17px] [&_svg]:w-[17px]">
                  <SelectedIcon aria-hidden="true" />
                </span>
              )}
              <div className="min-w-0">
                <h3 className="m-0 text-[17px] leading-[1.25] text-ink">
                  {selected ? selected.title : kind === 'call' ? 'This call prep' : 'This draft'}
                </h3>
                <p className="m-0 mt-[3px] text-[12.5px] leading-[1.4] text-dim">
                  {selected
                    ? `Step ${selectedIndex + 1} of ${total} · the code that ran, then the data it held`
                    : lead?.name || 'Provenance'}
                </p>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {selected ? (
              <>
                {/* THE CODE — first and whole. One block per execution
                    record: the lambda's deployed handler.py by symbol, or the
                    browser file this build was made from. */}
                {execRecords.length > 0 ? (
                  <div className="flex flex-col gap-y-3">
                    {execRecords.map((rec, i) => (
                      <ExecSource key={`${rec.nodeId || ''}-${rec.function || rec.fn || rec.file || ''}-${i}`} rec={rec} />
                    ))}
                  </div>
                ) : (
                  <p className="m-0 rounded-[12px] bg-panel p-3 text-[12.5px] leading-[1.5] text-dim">
                    This step carries no execution record on this receipt, so there is no source to show. Nothing is inferred in its place.
                  </p>
                )}

                {/* EVERYTHING ELSE, folded: the run's own sentence, the values
                    it held, the pages it read, the email. Same content the
                    view always had, one click away from the code. */}
                <details className="group mt-3 rounded-[12px] bg-panel">
                  <summary className="flex cursor-pointer list-none items-center gap-x-2 px-3 py-[10px] text-[12.5px] font-semibold text-dim hover:text-ink">
                    <span aria-hidden="true" className="text-faint transition-transform group-open:rotate-90">
                      ▸
                    </span>
                    Data this step held
                  </summary>
                  <div className="px-3 pb-3">
                    {selected.recorded && (
                      <PanelBlock label={`Recorded by the run at “${selected.recorded.nodeTitle}”`}>
                        <div className="rounded-[10px] bg-surface px-3 py-[10px]">
                          <p className="m-0 text-[13px] leading-[1.5] text-ink">{selected.recorded.note}</p>
                          {selected.recorded.verdict && (
                            <span className="mt-[7px] inline-flex items-center rounded-full bg-panel px-[8px] py-px text-[11px] font-semibold uppercase tracking-[0.04em] text-dim">
                              {selected.recorded.verdict}
                            </span>
                          )}
                        </div>
                      </PanelBlock>
                    )}
                    <PanelBlock label="What this step read">
                      <p className="m-0 text-[13.5px] leading-[1.5] text-ink">{selected.headline}</p>
                    </PanelBlock>
                    {selected.metric && (
                      <PanelBlock label="Result">
                        <div className="inline-flex items-baseline gap-x-[7px] rounded-[10px] bg-surface px-3 py-[9px]">
                          <span className="text-[19px] font-bold tabular-nums leading-none text-ink">
                            {Number(selected.metric.value).toLocaleString()}
                          </span>
                          <span className="text-[12.5px] text-dim">{selected.metric.label}</span>
                        </div>
                      </PanelBlock>
                    )}
                    {Array.isArray(selected.detail) && selected.detail.length > 0 && (
                      <PanelBlock label="The values it held">
                        <ul className="m-0 flex list-none flex-col gap-y-[7px] p-0">
                          {selected.detail.map((line, i) => (
                            <li key={i} className="flex items-start gap-x-[9px]">
                              <span aria-hidden="true" className="mt-[7px] h-[4px] w-[4px] flex-none rounded-full bg-line" />
                              <span className="text-[13px] leading-[1.5] text-dim">{line}</span>
                            </li>
                          ))}
                        </ul>
                      </PanelBlock>
                    )}
                    {Array.isArray(selected.links) && selected.links.length > 0 && (
                      <PanelBlock label="Sources the research read">
                        <ul className="m-0 flex list-none flex-col gap-y-[6px] p-0">
                          {selected.links.map((l) => (
                            <li key={l.href} className="m-0">
                              <a
                                href={l.href}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-[13px] font-semibold text-primary underline decoration-dotted underline-offset-2"
                              >
                                {l.host}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </PanelBlock>
                    )}
                    {showsEmail && (
                      <PanelBlock label="The draft">
                        <EmailPreview draft={draft} />
                      </PanelBlock>
                    )}
                    {showsCallPrep && (
                      <PanelBlock label="The call prep">
                        <CallPrepPanel meta={meta} />
                      </PanelBlock>
                    )}
                  </div>
                </details>
              </>
            ) : (
              <>
                <p className="m-0 text-[13.5px] leading-[1.55] text-dim">{K.summaryLine}</p>
                {kind === 'call' ? (
                  <PanelBlock label="The call prep">
                    <CallPrepPanel meta={meta} />
                  </PanelBlock>
                ) : (
                  <PanelBlock label="The draft">
                    <EmailPreview draft={draft} />
                  </PanelBlock>
                )}
              </>
            )}
          </div>

          <div className="flex flex-none flex-wrap items-center gap-2 px-5 py-[12px] [border-top:1px_solid_var(--border)]">
            <span className="inline-flex items-center gap-x-[7px] rounded-[9px] bg-panel px-3 py-[7px] text-[12.5px] font-semibold text-dim">
              {K.footer}
            </span>
          </div>
        </aside>
      </div>
      )}
    </section>
  )
}
